/**
 * Restricted QA Access + Impersonation feature-flag / status hook.
 *
 * Reads `/api/qa-access/flags` (super_admin gated) and
 * `/api/impersonation/status` (any signed-in user) so the app can render:
 *   - the "Viewing as ..." banner for impersonated sessions
 *   - Super Admin QA Access controls when the flag is on
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEFAULTS = {
  PRODUCTION_QA_ACCESS_ENABLED: false,
  SUPER_ADMIN_IMPERSONATION_ENABLED: false,
};

export function useQaAccessFlags() {
  const [flags, setFlags] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const refresh = useCallback(() => {
    setLoading(true);
    return api
      .get("/qa-access/flags")
      .then((r) => setFlags({ ...DEFAULTS, ...(r.data?.flags || {}) }))
      .catch(() => setFlags(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }   // super_admin-only — skip for anon/public visitors
    let alive = true;
    api
      .get("/qa-access/flags")
      .then((r) => alive && setFlags({ ...DEFAULTS, ...(r.data?.flags || {}) }))
      .catch(() => alive && setFlags(DEFAULTS))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user]);

  return {
    flags,
    loading,
    qaAccessEnabled: !!flags.PRODUCTION_QA_ACCESS_ENABLED,
    impersonationEnabled: !!flags.SUPER_ADMIN_IMPERSONATION_ENABLED,
    refresh,
  };
}

export function useImpersonationStatus() {
  const [status, setStatus] = useState({ impersonating: false });
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const refresh = useCallback(() => {
    return api
      .get("/impersonation/status")
      .then((r) => setStatus(r.data || { impersonating: false }))
      .catch(() => setStatus({ impersonating: false }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!user) { setLoading(false); return; }   // requires a signed-in session — skip for anon/public visitors
    let alive = true;
    api
      .get("/impersonation/status")
      .then((r) => alive && setStatus(r.data || { impersonating: false }))
      .catch(() => alive && setStatus({ impersonating: false }))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user]);

  return { ...status, loading, refresh };
}
